import React, { useEffect } from 'react';
import { toEventLocalISOString } from '../utils/eventDateTime';
import { isIndexableDeployment } from '../config/deploymentEnvironment';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  type?: 'website' | 'article' | 'event';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  structuredData?: object[];
  noIndex?: boolean;
  locale?: string;
  languageAlternates?: Array<{ hrefLang: string; href: string }>;
}

const EMPTY_LANGUAGE_ALTERNATES: Array<{ hrefLang: string; href: string }> = [];

const defaultProps: Partial<SEOProps> = {
  title: 'Event Finder | Discover Events Near You and Online',
  description: 'Discover events worldwide with our AI-powered event discovery platform. Find conferences, workshops, training sessions, and networking events near you.',
  keywords: 'events, event finder, conferences, workshops, training, AI-powered search, event discovery, networking events',
  image: '/EFlogo.png',
  imageAlt: 'Event Finder logo',
  imageWidth: 465,
  imageHeight: 458,
  type: 'website',
  locale: 'en_US'
};

export const SEO: React.FC<SEOProps> = (props) => {
  const {
    title = defaultProps.title,
    description = defaultProps.description,
    keywords = defaultProps.keywords,
    canonical,
    image = defaultProps.image,
    imageAlt = defaultProps.imageAlt,
    imageWidth = defaultProps.imageWidth,
    imageHeight = defaultProps.imageHeight,
    type = defaultProps.type,
    publishedTime,
    modifiedTime,
    author,
    structuredData = [],
    noIndex = false,
    locale = defaultProps.locale,
    languageAlternates = EMPTY_LANGUAGE_ALTERNATES
  } = props;

  const fullTitle = title?.includes('Event Finder') ? title : `${title} | Event Finder`;
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.eventfinder.site';
  const currentUrl = canonical || (typeof window !== 'undefined' ? window.location.href : siteUrl);
  const fullImageUrl = image?.startsWith('http') ? image : `${siteUrl}${image}`;

  useEffect(() => {
    document.querySelectorAll('script[data-static-seo]').forEach((element) => element.remove());
    const previousLanguageAlternates = languageAlternates.length
      ? Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]')).map((element) => ({
          hrefLang: element.getAttribute('hreflang') || '',
          href: (element as HTMLLinkElement).href,
        }))
      : [];

    // Update document title
    document.title = fullTitle;

    // Function to update or create meta tag
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Function to update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // Update primary meta tags
    updateMetaTag('title', fullTitle);
    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    updateMetaTag(
      'robots',
      noIndex || !isIndexableDeployment
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    );

    // Update canonical link
    if (canonical) {
      updateLinkTag('canonical', canonical);
    } else {
      document.querySelector('link[rel="canonical"]')?.remove();
    }

    if (languageAlternates.length) {
      document
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((element) => element.remove());
      languageAlternates.forEach(({ hrefLang, href }) => {
        const element = document.createElement('link');
        element.rel = 'alternate';
        element.href = href;
        element.setAttribute('hreflang', hrefLang);
        element.setAttribute('data-seo-language-alternate', 'true');
        document.head.appendChild(element);
      });
    }

    // Update Open Graph tags
    if (type) updateMetaTag('og:type', type, true);
    updateMetaTag('og:title', fullTitle, true);
    if (description) updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:site_name', 'Event Finder', true);
    if (locale) updateMetaTag('og:locale', locale, true);
    
    if (image) {
      updateMetaTag('og:image', fullImageUrl, true);
      updateMetaTag('og:image:alt', imageAlt || '', true);
      updateMetaTag('og:image:width', String(imageWidth || 465), true);
      updateMetaTag('og:image:height', String(imageHeight || 458), true);
    }

    // Article specific tags
    if (type === 'article') {
      if (publishedTime) updateMetaTag('article:published_time', publishedTime, true);
      if (modifiedTime) updateMetaTag('article:modified_time', modifiedTime, true);
      if (author) updateMetaTag('article:author', author, true);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:site', '@eventfinder');
    updateMetaTag('twitter:creator', '@eventfinder');
    updateMetaTag('twitter:title', fullTitle);
    if (description) updateMetaTag('twitter:description', description);
    
    if (image) {
      updateMetaTag('twitter:image', fullImageUrl);
      updateMetaTag('twitter:image:alt', imageAlt || '');
    }

    // Add structured data
    structuredData.forEach((data, index) => {
      const scriptId = `structured-data-${index}`;
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    });

    // Cleanup function to remove dynamic structured data scripts
    return () => {
      structuredData.forEach((_, index) => {
        const scriptId = `structured-data-${index}`;
        const script = document.getElementById(scriptId);
        if (script) {
          script.remove();
        }
      });
      document
        .querySelectorAll('link[data-seo-language-alternate="true"]')
        .forEach((element) => element.remove());
      previousLanguageAlternates.forEach(({ hrefLang, href }) => {
        const element = document.createElement('link');
        element.rel = 'alternate';
        element.href = href;
        element.setAttribute('hreflang', hrefLang);
        document.head.appendChild(element);
      });
    };
  }, [
    fullTitle,
    description,
    keywords,
    canonical,
    image,
    fullImageUrl,
    imageAlt,
    imageWidth,
    imageHeight,
    type,
    publishedTime,
    modifiedTime,
    author,
    structuredData,
    noIndex,
    locale,
    languageAlternates,
    currentUrl
  ]);

  // This component doesn't render anything
  return null;
};

// Common structured data schemas
export const createEventStructuredData = (event: any) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  "name": event.title,
  "description": event.description,
  "startDate": toEventLocalISOString(event.startDate),
  "endDate": toEventLocalISOString(event.endDate),
  "eventStatus": "https://schema.org/EventScheduled",
  "eventAttendanceMode": event.isVirtual 
    ? "https://schema.org/OnlineEventAttendanceMode"
    : "https://schema.org/OfflineEventAttendanceMode",
  "location": event.isVirtual ? {
    "@type": "VirtualLocation",
    "url": event.eventUrl
  } : {
    "@type": "Place",
    "name": event.venueName,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": event.city,
      "addressRegion": event.state,
      "addressCountry": event.country
    }
  },
  ...(event.organizerName ? { "organizer": {
    "@type": "Organization",
    "name": event.organizerName,
    "url": event.organizerUrl
  } } : {}),
  "offers": event.price ? {
    "@type": "Offer",
    "price": event.price,
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "url": event.registrationUrl
  } : undefined,
  "image": event.imageUrl,
  "url": event.eventUrl
});

export const createBreadcrumbStructuredData = (breadcrumbs: Array<{name: string, url: string}>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": breadcrumb.name,
    "item": breadcrumb.url
  }))
});

export const createFAQStructuredData = (faqs: Array<{question: string, answer: string}>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const createItemListStructuredData = (items: Array<{name: string, url: string, position?: number}>, listName: string) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": listName,
  "numberOfItems": items.length,
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": item.position || index + 1,
    "name": item.name,
    "url": item.url
  }))
});

export default SEO;